/* eslint-disable spire/export-styles */
import * as styleConstants from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    reverse = "reverse",
    background = "background",
    paddingTop = "paddingTop",
    paddingBottom = "paddingBottom",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.reverse]: boolean;
        [fields.paddingTop]: number;
        [fields.paddingBottom]: number;
        [fields.background]: 'none' | 'lightBlue' | 'lightGreen' | 'grey';
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [12, 12, 1, 1, 1],
    2: [12, 12, 2, 2, 2],
    3: [12, 12, 3, 3, 3],
    4: [12, 12, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const backgroundColors = new Map([
    ['none', 'transparent'],
    ['lightBlue', styleConstants.colours.lightBlue],
    ['lightGreen', styleConstants.colours.lightGreen],
    ['grey', styleConstants.colours.darkGrey],
]);
/**
 * When background colour used, add addtional padding
 */
const addPadding = (paddingTop = 0, paddingBottom = 0, background = "none") =>{
    const hasBg = background ? background !== "none" : false;
    let rtnStr = ``;
    const paddingSm = 20;
    const paddingMd = 30;
    const paddingLg = 40;
    if(hasBg){
        rtnStr = `
        padding: ${styleHelpers.rem(paddingTop + paddingSm)}  ${styleHelpers.rem(paddingSm)}  ${styleHelpers.rem(paddingBottom + paddingSm)};
        @media (min-width: ${styleHelpers.rem(768)}){
            padding: ${styleHelpers.rem(paddingTop + paddingSm)}  ${styleHelpers.rem(paddingMd)}  ${styleHelpers.rem(paddingBottom + paddingSm)};
        }
        @media (min-width: ${styleHelpers.rem(1280)}){
            padding: ${styleHelpers.rem(paddingTop + paddingLg)}  ${styleHelpers.rem(paddingLg)}  ${styleHelpers.rem(paddingBottom + paddingLg)};
        }
        `;
    }else{
        rtnStr = `
        padding: ${styleHelpers.rem(paddingTop + paddingSm)}  0  ${styleHelpers.rem(paddingBottom + paddingSm)};
        `;
    }
    return rtnStr;
}

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow, reverse = false, background, paddingTop, paddingBottom},
        id,
    } = props;

    const columnElements: JSX.Element[] = [];
    
	const { values } = baseTheme.breakpoints;
	const rowSetup = css`
        position: relative;
        width:auto !important;
        z-index:0;
        ${addPadding(paddingTop, paddingBottom, background)}
        margin: 0 ${styleHelpers.rem(-10 - gap)};
        gap: ${styleHelpers.rem(20)} 0;
        @media (min-width: ${styleHelpers.rem(768)}){
            margin: 0 ${styleHelpers.rem(-20 - gap)};
			flex-direction : row${reverse ? "-reverse" : ""};
            gap: ${styleHelpers.rem(30)} 0;
        }
        @media (min-width: ${styleHelpers.rem(1024)}){
            margin: 0 ${styleHelpers.rem(-25 - gap)};
        }
        @media (min-width: ${styleHelpers.rem(1280)}){
            margin: 0 ${styleHelpers.rem(-30 - gap)};
            gap: ${styleHelpers.rem(40)} 0;
        }
        &::before{
            position:absolute;
            top: 0;
            bottom: 0;
            background-color: ${backgroundColors.get(props.fields.background) || 'transparent'};
            z-index: -1;
            content: "";
            left:${styleHelpers.rem(10)};
            right: ${styleHelpers.rem(10)};
            @media (min-width: ${styleHelpers.rem(768)}){
                left:${styleHelpers.rem(20)};
                right: ${styleHelpers.rem(20)};
            }
            @media (min-width: ${styleHelpers.rem(1024)}){
                left:${styleHelpers.rem(25)};
                right: ${styleHelpers.rem(25)};
            }
            @media (min-width: ${styleHelpers.rem(1280)}){
                left:${styleHelpers.rem(30)};
                right: ${styleHelpers.rem(30)};
            }
        }
	`;
    const columnCss = css`
    padding: 0 ${styleHelpers.rem(10 + gap)};
    @media (min-width: ${styleHelpers.rem(768)}){
        padding: 0 ${styleHelpers.rem(20 + gap)};
    }
    @media (min-width: ${styleHelpers.rem(1024)}){
        padding: 0 ${styleHelpers.rem(25 + gap)};
    }
    @media (min-width: ${styleHelpers.rem(1280)}){ 
        padding: 0 ${styleHelpers.rem(30 + gap)};
    }
    `;

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem css={columnCss} key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>
        );
    }
    // todo: pick up here, so items can get global column paddings.

    return <GridContainer offsetCss={rowSetup} gap={0}>{columnElements}</GridContainer>;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.reverse,
                displayName: "On viewport larger than 768px, should the columns reverse positions?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
				fieldType: "General",
				name: fields.background,
				editorTemplate: "DropDownField",
				displayName: "Background",
				defaultValue: "none",
				options: [
                    { displayName: "none", value: "none" },
                    { displayName: "light blue", value: "lightBlue" },
                    { displayName: "light green", value: "lightGreen" },
                    { displayName: "Grey", value: "grey" },
                ],
				hideEmptyOption: true,
			},
            {
                name: fields.paddingTop,
                displayName: "padding top",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
            },
            {
                name: fields.paddingBottom,
                displayName: "padding bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            }
        ],
    },
};

export default widgetModule;
